/**
 * PortfolioGrowthChart helpers — extracted for unit-testability.
 *
 * Phase 8.E.4 visual-review (polish pass) + mobile-container refit.
 *
 * NOTE: pure functions + one React hook. The hook is exported so
 * component-level tests can mock ResizeObserver in isolation.
 */
import React from "react";

/** Choose a user-actionable empty state without guessing transaction dates. */
export function performanceEmptyState(perf, refreshPending = false) {
  const reason = perf?.performance_method_reason || "";
  if (refreshPending || reason === "snapshot_refresh_pending") return "updating";
  if (/ambiguous_transactions$/.test(reason)) {
    const ambiguous = (perf?.diagnostics?.transaction_classifications || [])
      .filter((row) => row.classification === "ambiguous_excluded");
    if (ambiguous.length && ambiguous.every((row) => row.source === "user_additional_purchase"
      && row.reason === "null_transaction_date_and_source_user_additional_purchase_not_user_opening_lot")) {
      return "missing_dates";
    }
    return "transactions";
  }
  if (!perf || ["no_snapshots_in_range", "insufficient_valid_snapshots"].includes(reason)) return "history";
  return perf.performance_history_start_date ? "history" : "baseline";
}

/**
 * Compute a Y-axis domain with dynamic vertical headroom so that
 * tooltip dots and line strokes never clip the plotting-area border.
 *
 *   * values     numeric array (may contain nulls / undefined / NaN)
 *   * topPad     fraction of value span added ABOVE the max
 *                (default 0.08 → within the 5–10 % visual review target)
 *   * bottomPad  fraction of value span added BELOW the min
 *
 * Returns Recharts-compatible ``[min, max]`` tuple, OR
 * ``["dataMin", "dataMax"]`` when the series is degenerate so
 * Recharts can fall back to its own heuristic without crashing.
 */
export function computeYDomainWithHeadroom(values, { topPad = 0.08, bottomPad = 0.03 } = {}) {
  const nums = (values || []).filter(
    (v) => v !== null && v !== undefined && Number.isFinite(v),
  );
  if (nums.length < 2) return ["dataMin", "dataMax"];
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = Math.abs(max - min);
  if (span === 0) {
    // Flat series → pad symmetrically by ±5 % of |max| (or 1 if 0).
    const magnitude = Math.max(Math.abs(max), 1) * 0.05;
    return [min - magnitude, max + magnitude];
  }
  const upper = max + span * topPad;
  const lower = min - span * bottomPad;
  return [lower, upper];
}

/**
 * Resolve axis-title strings + widths based on viewport size.
 *
 * On mobile (isMobile=true) we deliberately drop all three axis
 * titles ("Portfoliowert", "Index (Basis = 100)", "Fair Value")
 * because their meaning is already carried by the chart heading,
 * subtitle, KPI cards, legend, and tooltip. The reclaimed axis
 * width widens the actual plotting area.
 */
export function resolveAxisTitles({ isMobile, t }) {
  return {
    indexAxisTitle: isMobile ? null : t("portfolio.chart.index_axis_label"),
    valueAxisTitle: isMobile ? null : t("portfolio.chart.value_axis_label"),
    fvAxisTitle:    isMobile ? null : t("portfolio.chart.fair_value_axis_label"),
    // Widths after removing titles — leaves just enough for compact
    // tick labels ("$10k", "142", etc). Desktop widths bumped so the
    // rotated labels "Performance-Index (Basis = 100)" and
    // "Estimated Fair Value ($)" are never clipped horizontally.
    yAxisWidthIndex:    isMobile ? 28 : 60,
    yAxisWidthCurrency: isMobile ? 38 : 72,
  };
}


// ─────────────────────────────────────────────────────────────────
// Container-width mobile detection (v4 — callback-ref pattern).
//
// The Owner Console "Mobile" toggle constrains only the chart's
// parent container to 390 px — the browser window remains 1920 px.
// Viewport-based ``matchMedia`` therefore never fires.
//
// We use a CALLBACK ref (not a useState / useEffect combination) so
// the measurement happens SYNCHRONOUSLY the moment React attaches
// the DOM node. This eliminates the first-paint race where the
// initial render used the desktop layout because the effect hadn't
// run yet — the whole reason axis titles kept appearing on mobile.
//
// Returns ``[setRef, isMobile, containerWidth]``. Attach ``setRef``
// as the ``ref`` prop of the element you want measured.
// ``containerWidth`` is exposed so callers can clamp e.g. tooltip
// max-widths to the actual rendered card, not the viewport.
// ─────────────────────────────────────────────────────────────────
export function useContainerIsMobile(breakpointPx = 640) {
  const [state, setState] = React.useState({ isMobile: false, containerWidth: 0 });
  // Persistent handles so the callback ref can disconnect on unmount /
  // reassignment without leaking a stale observer.
  const nodeRef = React.useRef(null);
  const observerRef = React.useRef(null);

  const setRef = React.useCallback(
    (node) => {
      // Node changed → tear down old observer.
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      nodeRef.current = node;
      if (!node) return;
      // Synchronous initial measurement — runs the moment React
      // attaches the DOM node, BEFORE the first paint of the child
      // subtree finishes.
      const rect = node.getBoundingClientRect();
      const w0 = rect.width || 0;
      setState({ isMobile: w0 > 0 && w0 <= breakpointPx, containerWidth: w0 });
      if (typeof ResizeObserver === "undefined") {
        // Best-effort fallback for jsdom / very old browsers.
        const onResize = () => {
          const w = (nodeRef.current && nodeRef.current.getBoundingClientRect().width) || 0;
          setState({ isMobile: w > 0 && w <= breakpointPx, containerWidth: w });
        };
        window.addEventListener("resize", onResize);
        observerRef.current = { disconnect() { window.removeEventListener("resize", onResize); } };
        return;
      }
      const obs = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const w = (entry.contentRect && entry.contentRect.width) || 0;
          setState({ isMobile: w > 0 && w <= breakpointPx, containerWidth: w });
        }
      });
      obs.observe(node);
      observerRef.current = obs;
    },
    [breakpointPx],
  );

  return [setRef, state.isMobile, state.containerWidth];
}


// ─────────────────────────────────────────────────────────────────
// Y-axis TICK FORMATTERS.
//
// Never expose raw floating-point implementation details on chart
// axes. Index ticks round to integer (or 1 decimal only when the
// span is narrow); currency ticks use localized compact notation
// (e.g. "$10k" / "10.000 $").
// ─────────────────────────────────────────────────────────────────
export function formatIndexTick(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return "";
  const abs = Math.abs(v);
  // Narrow spans (near zero) → 1 decimal so ticks aren't all "0".
  if (abs > 0 && abs < 5) return Number(v).toFixed(1);
  return String(Math.round(Number(v)));
}

/**
 * Format a currency tick in a locale-aware compact way.
 *
 *   locale = "en"  → $10k, $12.5k, $1.2M
 *   locale = "de"  → 10 T$ (compact "T" = "Tausend"). We use $ as
 *                    the currency symbol because the app displays
 *                    everything in USD; only the number formatting
 *                    changes with locale.
 *
 * We deliberately avoid Intl.NumberFormat's currency style with
 * "compactDisplay" because Safari <15 mis-renders the K suffix.
 */
export function formatCurrencyTick(v, locale = "en") {
  if (v === null || v === undefined || Number.isNaN(v)) return "";
  const abs = Math.abs(Number(v));
  const sign = v < 0 ? "-" : "";
  let compact;
  if (abs >= 1_000_000_000) compact = `${(abs / 1_000_000_000).toFixed(1)}B`;
  else if (abs >= 1_000_000) compact = `${(abs / 1_000_000).toFixed(1)}M`;
  else if (abs >= 1_000)     compact = `${Math.round(abs / 1_000)}k`;
  else                       compact = `${Math.round(abs)}`;
  // De-noise: "10.0k" → "10k".
  compact = compact.replace(/\.0([kMB])$/, "$1");
  // Localized separator for the fractional dot: EN keeps "10.5k",
  // DE swaps to "10,5k" and uses a non-breaking space before "$".
  if (locale === "de") {
    compact = compact.replace(".", ",");
    return `${sign}${compact}\u00A0$`;
  }
  return `${sign}$${compact}`;
}
